import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import IngredientsMultiSelect from '../restaurant/IngredientsMultiSelect';
import NutritionEditor, { parseNutritionRowsFromItem } from '../restaurant/NutritionEditor';
import '../restaurant/css/NutritionEditor.css';
import { emptyNutritionRows, buildNutritionForSubmit } from '../restaurant/foodNutrition';
import { GROUP_COURSE_ROLES, resolveIngredientIdsFromMenuItem } from '../restaurant/onlineFoodItems';
import { normalizeIngredientOptions, extractFoodCategoryName, normalizeFoodCategories } from '../restaurant/restaurantApi';
import { processMenuImageFile } from '../restaurant/menuImageUtils';
import '../restaurant/css/Menu.css';
import {
  createMenuMasterItem,
  deactivateMenuMasterItem,
  fetchAdminMenuMaster,
  updateMenuMasterItem,
} from './menuMasterApi';
import axiosRestaurantInstance from '../restaurant/AxiosRestaurantInstance';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import './JuniorEatsMainMenuPage.css';
import './GlobalMenuMasterPage.css';
import { confirmAction } from '../restaurant/ui/feedback';

const EMPTY_FORM = () => ({
  foodItem: '',
  foodCategory: '',
  foodType: 'VEG',
  courseRole: 'MAIN',
  notes: '',
  description: '',
  status: 'ACTIVE',
  ingredientIds: [],
  nutrition: emptyNutritionRows(),
  imageFile: null,
  imageUrl: '',
});

const GlobalMenuMasterPage = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM());
  const [saving, setSaving] = useState(false);
  const [ingredientOptions, setIngredientOptions] = useState([]);
  const [ingredientsLoading, setIngredientsLoading] = useState(false);
  const [showIntroInfo, setShowIntroInfo] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dietFilter, setDietFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [search, setSearch] = useState('');
  const introInfoRef = useRef(null);
  const imageFileInputRef = useRef(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');

  const userId = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      return user?.userId ?? user?.User_Id ?? user?.id ?? null;
    } catch {
      return null;
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('Token');
      const res = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.restaurantsFoodCategories,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      const list = normalizeFoodCategories(res.data);
      const names = [
        ...new Set(
          list
            .map((row) =>
              typeof row === 'string' ? row.trim() : String(extractFoodCategoryName(row) || '').trim(),
            )
            .filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b));
      setCategories(names);
    } catch {
      setCategories([]);
    }
  }, []);

  const loadIngredients = useCallback(async () => {
    try {
      setIngredientsLoading(true);
      const token = localStorage.getItem('Token');
      const res = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.restaurantsIngredients,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setIngredientOptions(normalizeIngredientOptions(res.data?.ingredients));
    } catch {
      setIngredientOptions([]);
    } finally {
      setIngredientsLoading(false);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAdminMenuMaster({
        includeInactive: true,
        foodCategory: categoryFilter || undefined,
        foodType: dietFilter || undefined,
        courseRole: courseFilter || undefined,
        q: search.trim() || undefined,
      });
      setItems(data.items);
    } catch (err) {
      setItems([]);
      setError(err?.response?.data?.error || err?.message || 'Failed to load menu master');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, dietFilter, courseFilter, search]);

  useEffect(() => {
    loadCategories();
    loadIngredients();
  }, [loadCategories, loadIngredients]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!showIntroInfo) return undefined;
    const handlePointerDown = (event) => {
      if (introInfoRef.current && !introInfoRef.current.contains(event.target)) {
        setShowIntroInfo(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowIntroInfo(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showIntroInfo]);

  useEffect(() => {
    const file = form.imageFile;
    if (!file) {
      setImagePreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.imageFile]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM());
    setShowForm(true);
    setMessage('');
    setError('');
  };

  const openEdit = (item) => {
    setEditingId(item.masterFoodItemId);
    setForm({
      foodItem: item.foodItem,
      foodCategory: item.foodCategory,
      foodType: item.foodType || 'VEG',
      courseRole: item.courseRole || 'MAIN',
      notes: item.notes,
      description: item.description,
      status: item.status || 'ACTIVE',
      ingredientIds: Array.isArray(item.ingredientIds)
        ? item.ingredientIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
        : resolveIngredientIdsFromMenuItem(item),
      nutrition: parseNutritionRowsFromItem(item),
      imageFile: null,
      imageUrl: item.imageUrl || '',
    });
    setShowForm(true);
    setMessage('');
    setError('');
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM());
  };

  const handleImageInputChange = async (e) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!file) return;
    try {
      const processed = await processMenuImageFile(file);
      setForm((p) => ({ ...p, imageFile: processed }));
      setError('');
    } catch (err) {
      setError(err?.message || 'Could not process image');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.foodItem.trim() || !form.foodCategory.trim()) {
      setError('Name and category are required');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        foodItem: form.foodItem.trim(),
        foodCategory: form.foodCategory.trim(),
        foodType: form.foodType || 'VEG',
        courseRole: form.courseRole || null,
        description: form.description || '',
        notes: form.notes || '',
        status: form.status || 'ACTIVE',
        ingredientIds: form.ingredientIds || [],
        nutrition: buildNutritionForSubmit(form.nutrition),
        imageFile: form.imageFile || undefined,
        createdById: userId,
        updatedById: userId,
      };
      if (editingId) {
        await updateMenuMasterItem(editingId, payload);
        setMessage('Master item updated (linked restaurant menus synced).');
      } else {
        await createMenuMasterItem(payload);
        setMessage('Master item created.');
      }
      closeForm();
      await loadItems();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (item) => {
    const ok = await confirmAction({
      title: 'Deactivate master item?',
      message: `${item.foodItem} will be hidden from restaurant pickers. Existing restaurant links stay.`,
      confirmLabel: 'Deactivate',
    });
    if (!ok) return;
    try {
      await deactivateMenuMasterItem(item.masterFoodItemId);
      setMessage('Master item deactivated.');
      await loadItems();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Deactivate failed');
    }
  };

  return (
    <div className="jreats-main-menu-page">
      <div className="jreats-main-menu-page__header">
        <div>
          <h3 className="jreats-main-menu-page__title">
            Global Menu Master
            <button
              type="button"
              className="jreats-main-menu-page__info-btn"
              aria-label="About global menu master"
              onClick={() => setShowIntroInfo((v) => !v)}
            >
              <FaInfoCircle />
            </button>
          </h3>
          {showIntroInfo ? (
            <p className="jreats-main-menu-page__intro" ref={introInfoRef}>
              Platform catalog organized by category, veg/non-veg, and course. Restaurants adopt
              items into My Eats / Dine-In menus (linked) or add custom dishes.
            </p>
          ) : null}
        </div>
      </div>

      <div className="jreats-main-menu-page__toolbar">
        <input
          type="search"
          placeholder="Search items…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search master items"
        />
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} aria-label="Category">
          <option value="">All categories</option>
          {categories.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        <select value={dietFilter} onChange={(e) => setDietFilter(e.target.value)} aria-label="Diet">
          <option value="">All diets</option>
          <option value="VEG">Veg</option>
          <option value="NON-VEG">Non-Veg</option>
        </select>
        <select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} aria-label="Course">
          <option value="">All courses</option>
          {GROUP_COURSE_ROLES.map((role) => (
            <option key={role.value} value={role.value}>{role.label}</option>
          ))}
        </select>
        <button
          type="button"
          className="eats-admin-btn eats-admin-btn--secondary gmm-add-btn"
          onClick={openCreate}
        >
          Add master item
        </button>
      </div>

      {error ? <p className="menu-template-items-load-error">{error}</p> : null}
      {message ? <p className="jreats-main-menu-page__message">{message}</p> : null}
      {loading ? <p>Loading…</p> : null}

      {!loading && (
        <div className="orders-table-wrap">
          <table className="menu-table rt-no-cards">
            <thead>
              <tr>
                <th aria-hidden="true" />
                <th>Item</th>
                <th>Category</th>
                <th>Diet</th>
                <th>Course</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="eats-admin-table__empty">No master items yet.</td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.masterFoodItemId}>
                    <td className="menu-table-image-col">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt="" width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} />
                      ) : (
                        <span className="gmm-thumb-placeholder" aria-hidden="true">—</span>
                      )}
                    </td>
                    <td className="gmm-item-name" data-label="Item">{item.foodItem}</td>
                    <td data-label="Category">{item.foodCategory}</td>
                    <td data-label="Diet">
                      <span className={`gmm-diet gmm-diet--${item.foodType === 'NON-VEG' ? 'nonveg' : 'veg'}`}>
                        {item.foodType === 'NON-VEG' ? 'Non-Veg' : 'Veg'}
                      </span>
                    </td>
                    <td data-label="Course">{item.courseRole || '—'}</td>
                    <td data-label="Status">
                      <span className={`gmm-status gmm-status--${String(item.status || '').toLowerCase()}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="gmm-actions" data-label="Actions">
                      <button type="button" className="linkish-btn" onClick={() => openEdit(item)}>Edit</button>
                      {item.status === 'ACTIVE' ? (
                        <button type="button" className="linkish-btn linkish-btn--danger" onClick={() => handleDeactivate(item)}>
                          Deactivate
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showForm ? (
        <div className="menu-item-modal-backdrop" role="presentation" onClick={closeForm}>
          <div
            className="menu-item-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="global-menu-master-form-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="global-menu-master-form-title">{editingId ? 'Edit master item' : 'Add master item'}</h4>
            <form onSubmit={handleSave} className="menu-item-form">
              <label>
                Name
                <input
                  required
                  value={form.foodItem}
                  onChange={(e) => setForm((p) => ({ ...p, foodItem: e.target.value }))}
                />
              </label>
              <label>
                Category
                <select
                  required
                  value={form.foodCategory}
                  onChange={(e) => setForm((p) => ({ ...p, foodCategory: e.target.value }))}
                >
                  <option value="">Select…</option>
                  {categories.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </label>
              <label>
                Diet
                <select
                  value={form.foodType}
                  onChange={(e) => setForm((p) => ({ ...p, foodType: e.target.value }))}
                >
                  <option value="VEG">Veg</option>
                  <option value="NON-VEG">Non-Veg</option>
                </select>
              </label>
              <label>
                Course
                <select
                  value={form.courseRole}
                  onChange={(e) => setForm((p) => ({ ...p, courseRole: e.target.value }))}
                >
                  {GROUP_COURSE_ROLES.map((role) => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Description
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                />
              </label>
              <label>
                Status
                <select
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </label>
              <div>
                <span className="menu-form-label">Ingredients</span>
                <IngredientsMultiSelect
                  options={ingredientOptions}
                  selectedIds={form.ingredientIds}
                  onChange={(ids) => setForm((p) => ({ ...p, ingredientIds: ids }))}
                  loading={ingredientsLoading}
                />
              </div>
              <NutritionEditor
                value={form.nutrition}
                onChange={(nutrition) => setForm((p) => ({ ...p, nutrition }))}
              />
              <div>
                <span className="menu-form-label">Image (WebP)</span>
                {(imagePreviewUrl || form.imageUrl) ? (
                  <img
                    src={imagePreviewUrl || form.imageUrl}
                    alt=""
                    width={72}
                    height={72}
                    style={{ objectFit: 'cover', display: 'block', marginBottom: 8 }}
                  />
                ) : null}
                <input
                  ref={imageFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageInputChange}
                />
              </div>
              <div className="menu-item-modal-actions">
                <button type="button" onClick={closeForm}>Cancel</button>
                <button type="submit" className="add-button" disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default GlobalMenuMasterPage;
